import { Injector } from '@angular/core';

import { CompareValueComponent, CompareDurationComponent } from '@shared/components';
import { ReportBaseClass } from '@modules/reporting/models';
import { OutputValue } from '@shared/components/compare-value/model';
import { IAssetPropertyValue } from '@services/assets/asset.interfaces';
import { CompareValueService, FormField, PropertyService } from '@services';
import { SubscriberService, Feature } from '@services/subscriber/subscriber.service';
import { PropertyCategory, PropertyStyle } from '@services/property/property-model.interfaces';
import { inUseState } from '@services/property/in-use-state';

import { includes, isUndefined, map } from 'lodash';

export abstract class AssetReportBaseClass extends ReportBaseClass {
  public static PropertyValuesKey = 'propertyValues_';

  public propertyService: PropertyService = this.injector.get(PropertyService);
  public compareValueService: CompareValueService = this.injector.get(CompareValueService);

  constructor(protected injector: Injector) {
    super(injector);
  }

  /**
   * Returns filter field definitions for time-based filtering.
   * Includes clock time filters (always) and work time filters (when Work Schedule module is enabled).
   */
  public getTimeFilterFields(): FormField[] {
    const fields: FormField[] = [
      // Clock time duration filters
      {
        containerClass: 'report-field custom-data-field removable',
        title: this.translate.instant('PROPERTY.Total_Time_Unavailable'),
        name: 'filterTimeUnavailable',
        type: 'customElement',
        component: CompareDurationComponent,
        canDelete: true,
        onRemoveField: (field: any) => this.onRemoveField(field)
      },
      {
        containerClass: 'report-field custom-data-field removable',
        title: this.translate.instant('PROPERTY.Total_Time_Strained'),
        name: 'filterTimeStrained',
        type: 'customElement',
        component: CompareDurationComponent,
        canDelete: true,
        onRemoveField: (field: any) => this.onRemoveField(field)
      },
      {
        containerClass: 'report-field custom-data-field removable',
        title: this.translate.instant('PROPERTY.Total_Time_Available'),
        name: 'filterTimeAvailable',
        type: 'customElement',
        component: CompareDurationComponent,
        canDelete: true,
        onRemoveField: (field: any) => this.onRemoveField(field)
      },
      // Clock time percentage filters
      {
        containerClass: 'report-field custom-data-field removable',
        title: this.translate.instant('PROPERTY.Percent_Time_Unavailable'),
        name: 'filterPercentTimeUnavailable',
        type: 'customElement',
        component: CompareValueComponent,
        canDelete: true,
        onRemoveField: (field: any) => this.onRemoveField(field)
      },
      {
        containerClass: 'report-field custom-data-field removable',
        title: this.translate.instant('PROPERTY.Percent_Time_Strained'),
        name: 'filterPercentTimeStrained',
        type: 'customElement',
        component: CompareValueComponent,
        canDelete: true,
        onRemoveField: (field: any) => this.onRemoveField(field)
      },
      {
        containerClass: 'report-field custom-data-field removable',
        title: this.translate.instant('PROPERTY.Percent_Time_Available'),
        name: 'filterPercentTimeAvailable',
        type: 'customElement',
        component: CompareValueComponent,
        canDelete: true,
        onRemoveField: (field: any) => this.onRemoveField(field)
      }
    ];

    // Work time filters (conditional on module)
    if (this.subscriberService.usesFeature(Feature.WORK_SCHEDULE)) {
      fields.push(
        // Work time duration filters
        {
          containerClass: 'report-field custom-data-field removable',
          title: this.translate.instant('WORK_SCHEDULE.Total_Work_Time_Unavailable'),
          name: 'filterWorkTimeUnavailable',
          type: 'customElement',
          component: CompareDurationComponent,
          canDelete: true,
          onRemoveField: (field: any) => this.onRemoveField(field)
        },
        {
          containerClass: 'report-field custom-data-field removable',
          title: this.translate.instant('WORK_SCHEDULE.Total_Work_Time_Strained'),
          name: 'filterWorkTimeStrained',
          type: 'customElement',
          component: CompareDurationComponent,
          canDelete: true,
          onRemoveField: (field: any) => this.onRemoveField(field)
        },
        {
          containerClass: 'report-field custom-data-field removable',
          title: this.translate.instant('WORK_SCHEDULE.Total_Work_Time_Available'),
          name: 'filterWorkTimeAvailable',
          type: 'customElement',
          component: CompareDurationComponent,
          canDelete: true,
          onRemoveField: (field: any) => this.onRemoveField(field)
        },
        // Work time percentage filters
        {
          containerClass: 'report-field custom-data-field removable',
          title: this.translate.instant('WORK_SCHEDULE.Percent_Work_Time_Unavailable'),
          name: 'filterPercentWorkTimeUnavailable',
          type: 'customElement',
          component: CompareValueComponent,
          canDelete: true,
          onRemoveField: (field: any) => this.onRemoveField(field)
        },
        {
          containerClass: 'report-field custom-data-field removable',
          title: this.translate.instant('WORK_SCHEDULE.Percent_Work_Time_Strained'),
          name: 'filterPercentWorkTimeStrained',
          type: 'customElement',
          component: CompareValueComponent,
          canDelete: true,
          onRemoveField: (field: any) => this.onRemoveField(field)
        },
        {
          containerClass: 'report-field custom-data-field removable',
          title: this.translate.instant('WORK_SCHEDULE.Percent_Work_Time_Available'),
          name: 'filterPercentWorkTimeAvailable',
          type: 'customElement',
          component: CompareValueComponent,
          canDelete: true,
          onRemoveField: (field: any) => this.onRemoveField(field)
        }
      );
    }

    return fields;
  }

  // CVX-6547: Available Filters lists the property name with its folder path as a smaller line
  // beneath it; the full title is still the field label and the list's sort key
  private propertyFieldLabels(property) {
    const path = `${this.translate.instant('SHARED.Property')}: ${this.propertyService.folderPath(property)}`;
    return { title: `${path} / ${property.title}`, filterName: property.title, filterDescription: path };
  }

  public getAssetDynamicNumericPropertyFields() {
    const numericProperties = this.propertyService.getPropertiesByCategory([PropertyCategory.Numeric]);
    const inServiceProperties = this.propertyService.getPropertiesByCategory([PropertyCategory.InService]);

    // Numeric properties use CompareValueComponent for numeric comparison
    const numericFields = map(numericProperties, (property) => {
      return {
        containerClass: 'report-field custom-data-field removable',
        ...this.propertyFieldLabels(property),
        name: `${AssetReportBaseClass.PropertyValuesKey}${property.propertyID}`,
        type: 'customElement',
        component: CompareValueComponent,
        canDelete: true,
        onRemoveField: (field: any) => this.onRemoveField(field)
      } as FormField;
    });

    // InService properties use selectmenu with Available/Strained/Unavailable options
    const inServiceFields = map(inServiceProperties, (property) => {
      // Check if property uses 3-state (up/strained/down) or 2-state (up/down)
      const hasStrainedState = property.style === PropertyStyle.UpStrainedDown;
      const options = hasStrainedState
        ? [
            { id: 2, description: this.translate.instant('PROPERTY.State_Up') },
            { id: 1, description: this.translate.instant('PROPERTY.State_Strained') },
            { id: 0, description: this.translate.instant('PROPERTY.State_Down') }
          ]
        : [
            { id: 2, description: this.translate.instant('PROPERTY.State_Up') },
            { id: 0, description: this.translate.instant('PROPERTY.State_Down') }
          ];

      return {
        containerClass: 'report-field removable',
        ...this.propertyFieldLabels(property),
        name: `${AssetReportBaseClass.PropertyValuesKey}${property.propertyID}`,
        type: 'selectmenu',
        placeholder: this.translate.instant('PROPERTY.All_States'),
        multiple: true,
        canDelete: true,
        originalOrder: true,
        options,
        onRemoveField: (field: any) => this.onRemoveField(field)
      } as FormField;
    });

    const inUseFields = map(this.propertyService.getPropertiesByCategory([PropertyCategory.InUse]), (property) => ({
      containerClass: 'report-field removable',
      ...this.propertyFieldLabels(property),
      name: `${AssetReportBaseClass.PropertyValuesKey}${property.propertyID}`,
      type: 'selectmenu',
      placeholder: this.translate.instant('PROPERTY.All_States'),
      multiple: true,
      canDelete: true,
      originalOrder: true,
      options: [
        { id: 1, description: this.translate.instant('PROPERTY.State_InUse') },
        { id: 0, description: this.translate.instant('PROPERTY.State_NotInUse') }
      ],
      onRemoveField: (field: any) => this.onRemoveField(field)
    } as FormField));

    return [...numericFields, ...inServiceFields, ...inUseFields];
  }

  public isPropertyValueValid(event: IAssetPropertyValue, propertyValues: OutputValue | number[], propertyID: number) {
    let result = true;
    const property = this.propertyService.getPropertyById(propertyID);

    // InService properties use array of status IDs
    if (property?.category === PropertyCategory.InService) {
      if (Array.isArray(propertyValues) && propertyValues.length > 0) {
        const healthValue = this.propertyService.getPropertyHealthValue(event, propertyID, true);
        result = propertyValues.includes(healthValue);
      }
    } else if (property?.category === PropertyCategory.InUse) {
      if (Array.isArray(propertyValues) && propertyValues.length > 0) {
        result = propertyValues.includes(inUseState(event?.value));
      }
    } else if ((propertyValues as OutputValue)?.id) {
      // Numeric properties use CompareValueComponent output
      const eventValue = this.propertyService.getEventNumberValue(event, propertyID);

      if (!isUndefined(eventValue)) {
        result = this.compareValueService.matches(eventValue, propertyValues as OutputValue);
      }
    }

    return result;
  }
}
